import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, Notice, Spinner } from "../../ui/index.js";
import { mergeRows, rowValid, type ServiceRow, ServiceRowsEditor, toDraft } from "./ServiceRows.js";
import { SourcesPanel } from "./SourcesPanel.js";
import { MAX_SERVICES, type ParseDraft } from "./sources.js";

/**
 * «Заполнить из источника» на странице услуг (решение 04.11.2026: после мастера настройки прайс загружают здесь).
 * Тот же разбор, что на шаге «Услуги»: фото или скриншот прайса, PDF, Word, Excel, голосовое, текст. Модель видит
 * текущие услуги салона и предлагает новые и изменённые позиции; владелец проверяет их и сохраняет одной кнопкой.
 * Сохранение как у мастера (`onboarding.commitServices`): то же название обновляет услугу, новое создаёт её
 * у всех активных мастеров; удалять услуги отсюда нельзя; черновик мастера настройки не трогаем (`fromServicesPage`).
 * Ответы на вопросы о правилах, которые модель тоже находит, здесь не используются.
 */
export function ServicesFromSources({ salonId, current }: { salonId: string; current: Array<{ name: string; priceRsd: number; durationMin: number }> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ServiceRow[] | null>(null);
  const state = useQuery(trpc.onboarding.state.queryOptions({ salonId }, { enabled: open }));
  const commit = useMutation(
    trpc.onboarding.commitServices.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.pathFilter());
        setRows(null);
      },
    }),
  );
  // Разбор принимает в черновике не больше 100 услуг (`ParseDraftSchema` на сервере): модель видит первые 100.
  const draft: ParseDraft = { services: current.slice(0, MAX_SERVICES), answers: {} };
  const valid = rows !== null && rows.length > 0 && rows.every(rowValid);

  return (
    <Card className="mt-6 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold">{t("services.fromSource.title")}</h2>
          <p className="text-sm text-slate-500">{t("services.fromSource.hint")}</p>
        </div>
        <Button variant="secondary" className="w-full lg:w-auto" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? t("services.fromSource.hide") : t("services.fromSource.open")}
        </Button>
      </div>
      {commit.data && rows === null && (
        <Notice kind="success">{t("services.fromSource.saved", { created: commit.data.created, updated: commit.data.updated })}</Notice>
      )}
      {open &&
        (state.isPending ? (
          <Spinner />
        ) : state.isError ? (
          <Notice kind="error">{t("common.error")}</Notice>
        ) : !state.data.priceParsing ? (
          <Notice>{t("onb.services.noModel")}</Notice>
        ) : rows === null ? (
          <>
            {current.length > MAX_SERVICES && <Notice>{t("services.fromSource.tooMany", { max: MAX_SERVICES })}</Notice>}
            <SourcesPanel
              salonId={salonId}
              voiceInput={state.data.voiceInput}
              draft={draft}
              onParsed={(r) => {
                commit.reset();
                setRows(mergeRows([], r.services).slice(0, MAX_SERVICES));
              }}
            />
          </>
        ) : (
          <div className="space-y-3">
            {rows.length === 0 ? (
              <Notice>{t("services.fromSource.nothing")}</Notice>
            ) : (
              <Notice>{t("services.fromSource.review", { count: rows.length })}</Notice>
            )}
            <ServiceRowsEditor rows={rows} onChange={setRows} canAdd={false} />
            {rows.length > 0 && !rows.every(rowValid) && <Notice kind="error">{t("onb.services.invalid")}</Notice>}
            {commit.isError && <Notice kind="error">{t("common.error")}</Notice>}
            <div className="flex gap-2">
              <Button variant="secondary" disabled={commit.isPending} onClick={() => setRows(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                className="flex-1 lg:flex-none"
                disabled={!valid || commit.isPending}
                onClick={() => rows && commit.mutate({ salonId, services: rows.map(toDraft), fromServicesPage: true })}
              >
                {t("services.fromSource.save")}
              </Button>
            </div>
          </div>
        ))}
    </Card>
  );
}
